import React, { useEffect, useState } from "react";
import "./newsEvents.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import EventSection from "./EventList";
import NewsSection from "./NewsList";
import GallerySection from "./GalleryGrid";
import VideoSection from "./VideoCarousel";
import ImageDialog from "./ImageDialog";

const emptyCollections = {
  events: [],
  news: [],
  videos: [],
  gallery: [],
};

const NewsEvents = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState(emptyCollections);
  const [status, setStatus] = useState("loading");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogImages, setDialogImages] = useState([]);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDescription, setDialogDescription] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadHomeContent = async () => {
      setStatus("loading");

      const [events, news, videos, gallery] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getEventsnews`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getnews`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getyoutubelinks`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getgallarydata`),
      ]);

      if (!mounted) return;

      const getData = (result) =>
        result.status === "fulfilled" && Array.isArray(result.value.data)
          ? result.value.data
          : [];

      setCollections({
        events: getData(events),
        news: getData(news),
        videos: getData(videos),
        gallery: getData(gallery),
      });
      setStatus(
        [events, news, videos, gallery].some((result) => result.status === "fulfilled")
          ? "ready"
          : "error"
      );
    };

    loadHomeContent();

    return () => {
      mounted = false;
    };
  }, []);

  const openImages = async (entityType, id, title, description) => {
    const endpoint =
      entityType === "events"
        ? `/api/admin/events/${id}/images`
        : `/api/admin/news/${id}/images`;

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`);
      setDialogImages(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDialogImages([]);
    }

    setDialogTitle(title);
    setDialogDescription(description);
    setType(entityType);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogImages([]);
    setDialogTitle("");
    setDialogDescription("");
    setType("");
  };

  const openCalendarEvent = (eventId) => {
    if (!eventId) return;
    navigate(`/2.0/calendar?event=event-${encodeURIComponent(eventId)}`);
  };

  return (
    <section className="ne-wrap">
      <div className="ne-header">
        <span className="section-kicker">What is happening</span>
        <h2>News, Events & Community Moments</h2>
        <p>
          Stay close to announcements, programs, highlights, and stories from
          the Udyog Connect network.
        </p>
      </div>

      {status === "error" && (
        <div className="ne-alert">
          Live updates are temporarily unavailable. Please try again shortly.
        </div>
      )}

      <div className="ne-feature-grid">
        <EventSection
          events={collections.events}
          loading={status === "loading"}
          onView={openCalendarEvent}
        />
        <GallerySection images={collections.gallery} loading={status === "loading"} />
      </div>

      <div className="ne-feature-grid ne-feature-grid-secondary">
        <NewsSection
          news={collections.news}
          loading={status === "loading"}
          onView={(id, title, summary) => openImages("news", id, title, summary)}
        />
        <VideoSection videos={collections.videos} loading={status === "loading"} />
      </div>

      <ImageDialog
        open={dialogOpen}
        onClose={closeDialog}
        images={dialogImages}
        title={dialogTitle}
        description={dialogDescription}
        type={type}
      />
    </section>
  );
};

export default NewsEvents;
